import type { ReactNode } from 'react'
import { cx } from '../controls/cx'
import { IconTile } from '../controls/IconTile'
import s from './RecordPeek.module.css'

export interface PeekPagerProps {
  /** The ids in the order the list is CURRENTLY showing them. */
  ids: string[]
  /** The record the peek is on. Not in `ids` ⇒ no pager is drawn at all. */
  current: string
  onNavigate: (id: string) => void
  /**
   * Names what moves: `subject="record"` gives "Previous record"/"Next record".
   * Omitted keeps the bare "Previous"/"Next" that seven of the eight desks
   * shipped — this exists so the promotion renames nothing in the
   * accessibility tree. `EntityList` is the one that named its subject, and it
   * is the better label; widening it to the rest is a deliberate change, not a
   * side effect of moving a file.
   */
  subject?: string
}

export interface PeekHeaderProps {
  pager?: PeekPagerProps
  /** What the header says when there is no pager — a list names its entity. */
  lead?: ReactNode
  /** The host's own controls, after the spacer and before Close. */
  actions?: ReactNode
  onClose: () => void
  className?: string
  testId?: string
}

/**
 * THE RECORD PEEK'S HEADER (#2406): walk the list, say where you are, leave.
 *
 * Eight desks drew this row against `apps/console/src/crm/crm.module.css` —
 * `ProjectsList`, `EntityList`, `CommerceDesk`, `ReturnsDesk`,
 * `ContractsDesk`, `PipelinePage`, `ListView`, `ServiceDesk`. Six of them also
 * retyped the arithmetic behind the arrows, character for character:
 *
 *     orderedIds[Math.max(0, i - 1)]
 *     orderedIds[Math.min(orderedIds.length - 1, i + 1)]
 *
 * and nothing in the suite had ever pressed either button. Two defects lived
 * in that clamp, in all six copies:
 *
 *  1. At the first record `Math.max(0, i - 1)` is `i`. Previous was offered,
 *     enabled, and navigated to the record already open. Same for Next at the
 *     last. `disabled` says the true thing, and says it to a screen reader too.
 *  2. When the open id is NOT in the list — a record filtered out from under
 *     the peek, which happens the moment a governed write moves it out of the
 *     view — `indexOf` is -1, `Math.max(0, -2)` is 0, and Previous jumped to
 *     the FIRST row. A pager that cannot state a position draws nothing here
 *     instead, because "1 of 12" about the wrong record is worse than silence.
 *
 * The three labels — Previous, Next, Close — are the originals, unchanged.
 * `e2e-browser/peek-pager.spec.ts` locates them by those words and was written
 * against the private markup before this component existed, so it measures the
 * move rather than describing it.
 */
export function PeekHeader({ pager, lead, actions, onClose, className, testId }: PeekHeaderProps) {
  const i = pager ? pager.ids.indexOf(pager.current) : -1
  const unit = pager?.subject ? ` ${pager.subject}` : ''
  return (
    <div className={cx(s.head, className)} data-peek="header" data-testid={testId}>
      {pager && i >= 0 && (
        <>
          <button type="button" aria-label={`Previous${unit}`} disabled={i === 0}
            onClick={() => { const p = pager.ids[i - 1]; if (p) pager.onNavigate(p) }}>↑</button>
          <button type="button" aria-label={`Next${unit}`} disabled={i === pager.ids.length - 1}
            onClick={() => { const n = pager.ids[i + 1]; if (n) pager.onNavigate(n) }}>↓</button>
          <span className={s.count}>{i + 1} of {pager.ids.length}</span>
        </>
      )}
      {lead != null && <span className={s.count}>{lead}</span>}
      <span className={s.spacer} />
      {actions}
      <button type="button" aria-label="Close" onClick={onClose}>✕</button>
    </div>
  )
}

export interface PeekBodyProps {
  children: ReactNode
  className?: string
  testId?: string
}

/** The scrolling half of a peek. The header does not scroll; this does. */
export function PeekBody({ children, className, testId }: PeekBodyProps) {
  return <div className={cx(s.body, className)} data-peek="body" data-testid={testId}>{children}</div>
}

export interface PeekIdentityProps {
  /** The entity mark. Rendered in an `xl` tile — six desks wrote that size by hand. */
  mark?: ReactNode
  title: ReactNode
  /** For the `tnum` utility: three desks title a peek with a reference number. */
  titleClassName?: string
  /** The subtitle row: the account, the stage, the chips. */
  children?: ReactNode
  className?: string
  testId?: string
}

/**
 * WHAT THE RECORD IS, at the top of the body: mark, title, subtitle row.
 *
 * `data-peek="sub"` is deliberate. Three browser specs located this row by
 * `[class*=psub]` — a CSS-module hash, i.e. a private class name promoted to a
 * test contract by accident. The attribute is the seam, so the next rename of
 * the sheet is not also a rename of the suite.
 */
export function PeekIdentity({ mark, title, titleClassName, children, className, testId }: PeekIdentityProps) {
  return (
    <div className={cx(s.identity, className)} data-peek="identity" data-testid={testId}>
      {mark != null && <IconTile size="xl">{mark}</IconTile>}
      <div>
        <h2 className={cx(s.title, titleClassName)}>{title}</h2>
        {children != null && <div className={s.sub} data-peek="sub">{children}</div>}
      </div>
    </div>
  )
}

export interface PeekShellProps {
  /** Open ⇒ on screen. Closed ⇒ parked off-screen, still mounted, still named. */
  open: boolean
  /** The panel's accessible name. There is no default — an unnamed complementary
   *  landmark is one a screen-reader user cannot tell from any other. */
  label: string
  children: ReactNode
  id?: string
  className?: string
  testId?: string
}

/**
 * THE PANEL THAT STOPS AT THE DRAWER'S EDGE (#2406 §5).
 *
 * `shell/DrawerShell.tsx` publishes its measured width as `--drawer-w` on the
 * document root. Until this component existed, the only readers were two
 * console feature stylesheets — a host reaching into another package's runtime
 * name, with no declared default, no contract and no test that could see the
 * coupling. The two copies had already drifted in width, z-index and elevation.
 *
 * So the geometry moves to the package that publishes the number. A host now
 * asks for a peek and gets one that is correct beside the drawer at every
 * width, including the compact breakpoint where the drawer publishes `0px`.
 */
export function PeekShell({ open, label, children, id, className, testId }: PeekShellProps) {
  return (
    <aside id={id} className={cx(s.shell, className)} data-open={open || undefined}
      data-peek="shell" aria-label={label} data-testid={testId}>
      {children}
    </aside>
  )
}
